import { useQueryClient } from '@tanstack/react-query';
import { Check, Trash2, Undo2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { getListLogsQueryKey } from '@/api/logs/logs';
import {
  getGetPlanQueryKey,
  useAddPlanFromDish,
  useAddPlanFromText,
  useApplyPlan,
  useApplyPlanItem,
  useGetPlan,
  useRemovePlanItem,
  useUnapplyPlanItem,
} from '@/api/plan/plan';
import { getGetDailySummaryQueryKey } from '@/api/summary/summary';
import type { PlanItemDto, RejectedPlanItemDto } from '@/api/model';
import { MedicalDisclaimer } from '@/app/layout';
import { DishPicker } from '@/components/dish-picker';
import { DishSuggester } from '@/components/dish-suggester';
import { Button } from '@/components/ui/button';
import { FilterChip } from '@/components/ui/chip';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/field';
import { cn, kcal, messageOf } from '@/lib/utils';

/** Below this the item was priced by a guess, and the row says so (spec §10). */
const CONFIRM_THRESHOLD = 0.6;

const REJECTION_COPY: Record<string, string> = {
  'not-food': 'ไม่ใช่รายการอาหาร',
  unknown: 'ยังไม่มีข้อมูลอาหารนี้',
};

/**
 * The standing meal plan: what the user thinks they will eat, priced but not
 * yet logged.
 *
 * Nothing here moves the day's total until an item is applied — that is the
 * whole point of planning separately from logging — so the screen leads with
 * the projection ("if I eat this, where do I land") and keeps applying one tap
 * away.
 */
export function Plan() {
  const queryClient = useQueryClient();
  const plan = useGetPlan();

  const [mode, setMode] = useState<'text' | 'dish' | 'suggest'>('text');
  const [text, setText] = useState('');
  const [rejected, setRejected] = useState<RejectedPlanItemDto[]>([]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetPlanQueryKey() }),
      // Applying writes food logs, so the Today card and its list are stale too.
      queryClient.invalidateQueries({ queryKey: getGetDailySummaryQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListLogsQueryKey() }),
    ]);
  };

  const addText = useAddPlanFromText({
    mutation: {
      onSuccess: async (result) => {
        setText('');
        setRejected(result.rejected);
        await refresh();
      },
    },
  });
  const addDish = useAddPlanFromDish({
    mutation: {
      onSuccess: async () => {
        setRejected([]);
        await refresh();
      },
    },
  });
  const applyAll = useApplyPlan({ mutation: { onSuccess: refresh } });
  const applyItem = useApplyPlanItem({ mutation: { onSuccess: refresh } });
  const unapply = useUnapplyPlanItem({ mutation: { onSuccess: refresh } });
  const remove = useRemovePlanItem({ mutation: { onSuccess: refresh } });

  const submitText = (event: FormEvent) => {
    event.preventDefault();
    if (text.trim().length === 0) return;
    addText.mutate({ data: { text: text.trim() } });
  };

  const pending = plan.data?.pendingCount ?? 0;
  const overBudget = (plan.data?.projectedRemaining ?? 0) < 0;
  // What the plan still leaves room for — the number the user would type into
  // the suggester anyway. Left blank once the plan already fills the budget,
  // since "what fits in -400 kcal" is not a question worth prefilling.
  const remaining = plan.data?.projectedRemaining ?? null;
  const suggestFrom = remaining != null && remaining > 0 ? remaining : null;
  const failure = [addText.error, addDish.error, applyAll.error, applyItem.error].find(Boolean);

  return (
    <>
      <Card className="space-y-4">
        <CardTitle>ถ้ากินตามแผนนี้</CardTitle>

        <p className="text-4xl font-semibold tabular-nums">
          {kcal(plan.data?.planned.kcal ?? 0)}
          <span className="ml-1 text-base font-normal text-ink-muted">kcal ที่วางแผนไว้</span>
        </p>

        <dl className="grid grid-cols-3 gap-2 text-center">
          <Stat label="กินแล้ววันนี้" value={kcal(plan.data?.consumed)} />
          <Stat label="รวมทั้งวัน" value={kcal(plan.data?.projected)} />
          <Stat
            label={overBudget ? 'เกินงบ' : 'เหลือจากงบ'}
            value={
              plan.data?.projectedRemaining == null
                ? '—'
                : kcal(Math.abs(plan.data.projectedRemaining))
            }
            tone={overBudget ? 'warning' : undefined}
          />
        </dl>

        {plan.data?.budget == null && !plan.isPending && (
          <p className="text-center text-xs text-ink-muted">
            ยังไม่ได้ตั้งเป้าหมาย — ตั้งที่แท็บ &ldquo;เป้าหมาย&rdquo; เพื่อดูว่าแผนนี้พอดีงบไหม
          </p>
        )}

        <Button
          size="lg"
          onClick={() => applyAll.mutate()}
          disabled={pending === 0 || applyAll.isPending}
        >
          {applyAll.isPending ? 'กำลังลงบันทึก…' : `ลงบันทึกทั้งหมด (${pending})`}
        </Button>
      </Card>

      <Card className="space-y-3">
        <CardTitle>เพิ่มเข้าแผน</CardTitle>

        <div className="grid grid-cols-3 gap-2">
          <ModeTab active={mode === 'text'} onClick={() => setMode('text')}>
            พิมพ์เอง
          </ModeTab>
          <ModeTab active={mode === 'dish'} onClick={() => setMode('dish')}>
            ค้นหาเมนู
          </ModeTab>
          <ModeTab active={mode === 'suggest'} onClick={() => setMode('suggest')}>
            แนะนำ
          </ModeTab>
        </div>

        {mode === 'text' && (
          <form onSubmit={submitText} className="space-y-3">
            <Input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="เช่น ข้าวผัดกุ้ง 2 จาน, ชานมเย็น"
              aria-label="อาหารที่คิดว่าจะกิน"
            />
            <Button
              type="submit"
              size="lg"
              disabled={addText.isPending || text.trim().length === 0}
            >
              {addText.isPending ? 'กำลังคิดแคลอรี…' : 'เพิ่มเข้าแผน'}
            </Button>
          </form>
        )}

        {mode === 'dish' && (
          <DishPicker
            busy={addDish.isPending}
            placeholder="ค้นหาเมนู เช่น ข้าวผัด"
            onPick={(pick) => addDish.mutate({ data: pick })}
          />
        )}

        {mode === 'suggest' && (
          <DishSuggester
            busy={addDish.isPending}
            defaultKcal={suggestFrom}
            onPick={(pick) => addDish.mutate({ data: pick })}
          />
        )}

        {rejected.length > 0 && (
          <ul className="space-y-1 text-xs text-warning">
            {rejected.map((item) => (
              <li key={item.name}>
                ไม่ได้เพิ่ม &ldquo;{item.name}&rdquo; — {REJECTION_COPY[item.reason] ?? item.reason}
              </li>
            ))}
          </ul>
        )}

        {failure != null && <p className="text-xs text-danger">{messageOf(failure)}</p>}
      </Card>

      <Card className="space-y-3">
        <CardTitle>รายการในแผน</CardTitle>

        {plan.isPending && <p className="text-sm text-ink-muted">กำลังโหลด…</p>}

        {plan.data?.items.length === 0 && (
          <p className="text-sm text-ink-muted">
            ยังไม่มีรายการ — พิมพ์อาหารที่คิดว่าจะกิน แล้วกด &ldquo;ลงบันทึก&rdquo; เมื่อได้กินจริง
          </p>
        )}

        <ul className="divide-y divide-border-subtle">
          {plan.data?.items.map((item) => (
            <PlanRow
              key={item.id}
              item={item}
              busy={applyItem.isPending || unapply.isPending || remove.isPending}
              onApply={() => applyItem.mutate({ id: item.id })}
              onUnapply={() => unapply.mutate({ id: item.id })}
              onRemove={() => remove.mutate({ id: item.id })}
            />
          ))}
        </ul>
      </Card>

      <MedicalDisclaimer />
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warning' }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd
        className={cn('text-lg font-semibold tabular-nums', tone === 'warning' && 'text-warning')}
      >
        {value}
      </dd>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <FilterChip
      selected={active}
      onClick={onClick}
      className="justify-center whitespace-nowrap px-2"
    >
      {children}
    </FilterChip>
  );
}

/**
 * One planned dish. An applied row stays in the plan rather than disappearing:
 * the plan is meant to be reused, and "วางแผนอีกครั้ง" is how it comes back
 * without retyping tomorrow.
 */
function PlanRow({
  item,
  busy,
  onApply,
  onUnapply,
  onRemove,
}: {
  item: PlanItemDto;
  busy: boolean;
  onApply: () => void;
  onUnapply: () => void;
  onRemove: () => void;
}) {
  const applied = item.appliedLogId != null;
  const estimated = item.confidence != null && item.confidence < CONFIRM_THRESHOLD;

  return (
    <li className={cn('flex items-center gap-3 py-2.5', applied && 'opacity-60')}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {item.name}
          {item.qty !== 1 && <span className="text-ink-muted"> ×{item.qty}</span>}
        </p>
        {applied ? (
          <p className="text-xs text-ink-muted">ลงบันทึกแล้ว</p>
        ) : (
          estimated && <p className="text-xs text-warning">ค่าประมาณ</p>
        )}
      </div>

      <p className="shrink-0 text-sm tabular-nums">{kcal(item.kcal)} kcal</p>

      {applied ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onUnapply}
          disabled={busy}
          aria-label={`วางแผน ${item.name} อีกครั้ง`}
        >
          <Undo2 className="size-4" aria-hidden />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={onApply}
          disabled={busy}
          aria-label={`ลงบันทึก ${item.name}`}
        >
          <Check className="size-4" aria-hidden />
        </Button>
      )}

      <Button
        variant="danger"
        size="icon"
        onClick={onRemove}
        disabled={busy}
        aria-label={`ลบ ${item.name} ออกจากแผน`}
      >
        <Trash2 className="size-4" aria-hidden />
      </Button>
    </li>
  );
}
